import React, { useCallback, useEffect, useMemo, useState } from "react"
import { Sparkles, ChevronLeft, ChevronRight, Search } from "lucide-react"

import { AppShell } from "src/components/layout/AppShell"
import { Button } from "src/components/ui/button"
import { Input } from "src/components/ui/input"
import { cn } from "src/utils/cn"
import { useToast } from "src/components/ui/toast"

import {
  useQualifiedQueue,
  useQualificationDetail,
  useQualificationActions,
} from "src/features/qualification/hooks/useQualification"
import { QueueStats } from "src/features/qualification/components/QueueStats"
import { QualifiedQueueTable } from "src/features/qualification/components/QualifiedQueueTable"
import { QualificationDetailSheet } from "src/features/qualification/components/QualificationDetailSheet"
import { BAND_META } from "src/features/qualification/constants"

// Band tabs in priority order. "all" first, then hottest → coldest.
const TABS = [
  { id: "all", label: "All" },
  { id: "hot", label: "Hot" },
  { id: "warm", label: "Warm" },
  { id: "nurture", label: "Nurture" },
  { id: "junk", label: "Junk" },
]

export default function QualificationPage() {
  const { toast } = useToast()
  const { data, loading, fetch } = useQualifiedQueue()
  const { detail, loading: detailLoading, load: loadDetail, clear } = useQualificationDetail()
  const { rescore, pending: actionPending } = useQualificationActions()

  const [band, setBand] = useState("all")
  const [q, setQ] = useState("")
  const [page, setPage] = useState(1)
  const [sheetOpen, setSheetOpen] = useState(false)

  const filters = useMemo(
    () => ({ band: band === "all" ? "" : band, q, page, per_page: 25 }),
    [band, q, page]
  )

  // Debounced fetch on filter change.
  useEffect(() => {
    const t = setTimeout(() => fetch(filters), q ? 300 : 0)
    return () => clearTimeout(t)
  }, [filters, fetch, q])

  const counts = data?.counts || {}
  const stats = data?.stats
  const leads = data?.leads || []
  const meta = data?.meta

  const openLead = useCallback(
    (lead) => {
      setSheetOpen(true)
      loadDetail(lead.id)
    },
    [loadDetail]
  )

  const handleRescore = useCallback(
    async (id) => {
      try {
        await rescore(id)
        toast({ title: "Re-scoring queued", description: "The lead will refresh in a moment." })
        setTimeout(() => fetch(filters), 1500)
      } catch {
        toast({ title: "Couldn't re-score", variant: "destructive" })
      }
    },
    [rescore, toast, fetch, filters]
  )

  const changeBand = (id) => {
    setBand(id)
    setPage(1)
  }

  return (
    <AppShell>
      <div className="space-y-5 px-8 py-7">
        {/* ── Header ── */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3.5">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 bg-white shadow-sm">
              <Sparkles className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-xl font-semibold tracking-tight text-slate-900">Qualified Queue</h1>
              <p className="text-[13px] text-slate-500">
                Leads the AI has talked to and scored on PAIR — call the genuine ones first.
              </p>
            </div>
          </div>
        </div>

        {/* ── ROI stats ── */}
        <QueueStats stats={stats} />

        {/* ── Band tabs + search ── */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-1.5">
            {TABS.map((tab) => {
              const active = band === tab.id
              const meta = BAND_META[tab.id]
              const count = tab.id === "all" ? counts.all : counts[tab.id]
              return (
                <button
                  key={tab.id}
                  onClick={() => changeBand(tab.id)}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-[13px] font-medium transition-colors",
                    active
                      ? "border-slate-900 bg-slate-900 text-white"
                      : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                  )}
                >
                  {meta && <span className={cn("h-1.5 w-1.5 rounded-full", active ? "bg-white" : meta.dot)} />}
                  {tab.label}
                  {count != null && (
                    <span className={cn("tabular-nums", active ? "text-slate-300" : "text-slate-400")}>
                      {count}
                    </span>
                  )}
                </button>
              )
            })}
          </div>

          <div className="relative w-full max-w-xs">
            <Search
              style={{ left: "0.75rem" }}
              className="absolute top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none"
            />
            <Input
              style={{ paddingLeft: "2.5rem" }}
              value={q}
              onChange={(e) => {
                setQ(e.target.value)
                setPage(1)
              }}
              placeholder="Search name, phone, email…"
            />
          </div>
        </div>

        {/* ── Queue ── */}
        <QualifiedQueueTable leads={leads} loading={loading} onRowClick={openLead} />

        {/* ── Pagination ── */}
        {meta && meta.total_pages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-[13px] text-slate-500">
              Page {meta.page} of {meta.total_pages} · {meta.total} leads
            </p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= meta.total_pages}
                onClick={() => setPage((p) => p + 1)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      <QualificationDetailSheet
        open={sheetOpen}
        onOpenChange={(o) => {
          setSheetOpen(o)
          if (!o) clear()
        }}
        detail={detail}
        loading={detailLoading}
        onRescore={handleRescore}
        onRescorePending={actionPending}
        onScheduled={(lead) => {
          toast({ title: "Site visit booked", description: `Scheduled for ${lead?.name || "the lead"}.` })
          fetch(filters)
        }}
      />
    </AppShell>
  )
}
